import cloneDeep from 'lodash/cloneDeep';

/**
 * Takes the banner props and removes the mobile values. This is a useful work around to render the correct
 * banner with SSR. Because stellar components are mobile first and use device width which are not available on the server
 * @param {*} bannerProps props object for @hpstellar-core/Banners
 * @returns {*} Banner props with the mobile values removed
 */
export const removeMobileBannerProps = bannerProps =>
    bannerProps instanceof Array
        ? bannerProps.map(banner => {
              let newBanner = cloneDeep(banner);
              newBanner.titleMobile = null;
              newBanner.descriptionMobile = null;
              if (newBanner.image && newBanner.image.url) {
                  newBanner.image.url.mobile = null;
              }

              return newBanner;
          })
        : bannerProps;

export const elementIsOffscreen = (el, horizontalOffset = 0, verticalOffset = 0) => {
    try {
        let rect = el.getBoundingClientRect();
        return (
            rect.x + rect.width + horizontalOffset < 0 ||
            rect.y + rect.height + verticalOffset < 0 ||
            rect.x + horizontalOffset > window.innerWidth ||
            rect.y + verticalOffset > window.innerHeight
        );
    } catch (e) {}
};

export const clickOnEnter = e => {
    let charCode = e.which || e.charCode;
    if (charCode === 13 && e.currentTarget === e.target) {
        e.currentTarget.click();
    }
};

export const scrollToAndLock = (target, options = {}) => {
    const { top = 0, onComplete, timeout = 2500 } = options || {};

    if (!target) {
        if (typeof onComplete === 'function') onComplete();
        return () => {};
    }

    let locked = true;
    let programmatic = false;
    let ignoreNextScroll = true;

    function lockToTarget() {
        if (!locked) return;

        const currentTop = target.getBoundingClientRect().top;
        const delta = currentTop - top;

        if (Math.abs(delta) > 1) {
            programmatic = true;
            window.scrollBy(0, delta);

            requestAnimationFrame(() => {
                programmatic = false;
            });
        }
    }

    const observer =
        typeof ResizeObserver === 'function'
            ? new ResizeObserver(() => {
                  requestAnimationFrame(lockToTarget);
              })
            : { observe: () => {}, disconnect: () => {} };

    observer.observe(document.body);

    function unlockOnUserScroll() {
        if (!programmatic) {
            cleanup();
        }
    }

    function onScroll() {
        if (ignoreNextScroll) {
            ignoreNextScroll = false;
            return;
        }

        unlockOnUserScroll();
    }

    function cleanup() {
        locked = false;

        try {
            observer.disconnect();
        } catch (e) {}

        try {
            window.removeEventListener('wheel', unlockOnUserScroll);
            window.removeEventListener('touchmove', unlockOnUserScroll);
            window.removeEventListener('keydown', unlockOnUserScroll);
            window.removeEventListener('scroll', onScroll);
        } catch (e) {}

        if (typeof onComplete === 'function') {
            onComplete();
        }
    }

    setTimeout(() => {
        cleanup();
    }, timeout);

    target.scrollIntoView();

    window.addEventListener('wheel', unlockOnUserScroll, { passive: true });
    window.addEventListener('touchmove', unlockOnUserScroll, { passive: true });
    window.addEventListener('keydown', unlockOnUserScroll);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
        cleanup();
    };
};
